// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import color, { getSystemColor } from "@stoneage/XDL/Utilities/color";
import * as React from "react";
import { css } from "styled-components";
import { mergeWithCss } from "@stoneage/XDL/Utilities/mergeWithCss";
import { fontConfig } from "@stoneage/XDL/constants";

export const iconLinkCommonStyles = (iconSrc: React.ComponentType | string) => {
    return {
        color: "primary",
        icon: {
            iconProps: {
                src: iconSrc,
                size: 13,
                color: "primary",
                css: css`
                    margin-right: 4px;
                    opacity: 0.33;
                `,
            },
            position: "left",
        },
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 12px;
            `,
        },
        css: css`
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
            &:disabled {
                &,
                & ${TypographyStyle}, & ${IconWrapper} {
                    color: ${color("primary", 0.5)};
                }
            }
            &:hover {
                & ${TypographyStyle}, & ${IconWrapper} {
                    color: ${color("primary")};
                }
                & ${IconWrapper} {
                    opacity: 1;
                }
            }
        `,
    } as LinkPresentationProps;
};

const linkSizeVariants = {
    regular: {
        size: 16,
        lineHeight: "19px",
        iconSize: 16,
    },
    small: {
        size: 14,
        lineHeight: "17px",
        iconSize: 14,
    },
    xsmall: {
        size: 12,
        lineHeight: "14px",
        iconSize: 12,
    },
    xxsmall: {
        size: 10,
        lineHeight: "12px",
        iconSize: 10,
    },
};

export const createLinkIconStyles = ({
    icon,
    linkStyles = {},
    sizeVariant = "regular",
    variant = "primary",
}: {
    icon?: React.ComponentType | string;
    linkStyles?: LinkPresentationProps;
    sizeVariant?: "regular" | "small" | "xsmall" | "xxsmall";
    variant?: "primary" | "secondary" | "tertiary";
}) => {
    const marginPosition = linkStyles?.icon?.position === "right" ? "margin-left" : "margin-right";
    const color = variant === "primary" ? "common.ui1" : variant === "secondary" ? "secondary" : "common.ui3";
    return {
        ...mergeWithCss(
            {
                color,
                hoverStyle: {
                    color: getSystemColor("primary"),
                },
                typographyProps: {
                    size: linkSizeVariants[sizeVariant].size,
                    lineHeight: linkSizeVariants[sizeVariant].lineHeight,
                    weight: fontConfig.weight.medium,
                    css: css`
                        letter-spacing: 0;
                    `,
                },
                icon: {
                    iconProps: {
                        src: icon,
                        size: linkSizeVariants[sizeVariant].iconSize,
                        css: css`
                            ${marginPosition}: 8px;
                        `,
                    },
                    position: linkStyles?.icon?.position,
                },
                css: css`
                    ${linkStyles?.icon?.position === "right" &&
                    css`
                        width: fit-content;
                    `}
                    &[disabled] {
                        cursor: not-allowed;
                        & ${TypographyStyle} {
                            color: ${getSystemColor(color, 0.33)};
                        }
                        & ${IconWrapper} {
                            opacity: 0.33;
                            color: ${getSystemColor(color)};
                        }
                        &:hover {
                            & ${TypographyStyle} {
                                color: ${getSystemColor(color, 0.33)};
                            }
                            ${IconWrapper} {
                                opacity: 0.33;
                                color: ${getSystemColor(color)};
                            }
                        }
                    }
                `,
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};
